import React, { useMemo, useState } from 'react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import PackageCard from '../components/PackageCard';
import { categories, packages, vehicles, testimonials, stats, brand, whatsappLink } from '../mock';
import { Link } from 'react-router-dom';
import { ArrowRight, MessageCircle, MapPin, Phone, Mail, Star, ShieldCheck, HeadphonesIcon, Wallet, Users, Award, Facebook, Search } from 'lucide-react';
import { toast } from '../hooks/use-toast';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Label } from '../components/ui/label';

export default function Home() {
  const [activeCat, setActiveCat] = useState('All');
  const [searchQ, setSearchQ] = useState('');
  const filtered = useMemo(() => {
    let list = activeCat === 'All' ? packages : packages.filter((p) => p.category === activeCat);
    if (searchQ.trim()) list = list.filter((p) => (p.title + ' ' + p.highlights).toLowerCase().includes(searchQ.toLowerCase()));
    return list;
  }, [activeCat, searchQ]);
  const featured = filtered.slice(0, 9);

  const [form, setForm] = useState({ name: '', phone: '', email: '', destination: '', date: '', travellers: '', message: '' });
  const onSubmit = (e) => {
    e.preventDefault();
    if (!form.name || !form.phone) { toast({ title: 'Please fill name and phone', variant: 'destructive' }); return; }
    const stored = JSON.parse(localStorage.getItem('enquiries') || '[]');
    stored.unshift({ ...form, at: new Date().toISOString() });
    localStorage.setItem('enquiries', JSON.stringify(stored));
    toast({ title: 'Enquiry received!', description: 'Our team will reply within minutes via WhatsApp/Phone.' });
    setForm({ name: '', phone: '', email: '', destination: '', date: '', travellers: '', message: '' });
  };

  return (
    <div className="bg-white">
      <Header />

      {/* HERO */}
      <section className="hero-bg relative">
        <div className="max-w-6xl mx-auto px-5 lg:px-8 py-28 md:py-36 text-center text-white">
          <span className="eyebrow text-orange-300 border-orange-300/40 bg-orange-300/10">✦ Trusted Travel Partner from Varanasi ✦</span>
          <h1 className="mt-6 font-serif text-4xl md:text-6xl font-bold leading-tight">
            Discover Incredible <span className="accent-word">India</span><br /> with Goodwishes Tours
          </h1>
          <p className="mt-5 max-w-2xl mx-auto text-blue-50/85 text-base md:text-lg leading-relaxed">
            From the sacred ghats of Kashi to the snowy peaks of Kashmir & beaches of Goa — experience spiritual & leisure journeys with comfort, safety & best prices.
          </p>
          <div className="mt-8 flex flex-wrap items-center justify-center gap-4">
            <a href="#packages" className="btn-orange inline-flex items-center gap-2">Explore Packages <ArrowRight className="w-4 h-4" /></a>
            <a href={whatsappLink('Hello Goodwishes India Tours, I want tour package details')} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 bg-white/10 border border-white/25 hover:bg-white/20 px-5 py-2.5 rounded-full font-semibold transition-colors">
              <MessageCircle className="w-4 h-4" /> WhatsApp Now
            </a>
          </div>

          {/* Hero search */}
          <form onSubmit={(e) => { e.preventDefault(); const el = document.getElementById('packages'); if (el) el.scrollIntoView({ behavior: 'smooth' }); }} className="mt-10 max-w-2xl mx-auto bg-white/10 backdrop-blur border border-white/20 rounded-full p-1.5 pl-5 flex items-center gap-3 shadow-2xl">
            <Search className="w-5 h-5 text-orange-300 shrink-0" />
            <input value={searchQ} onChange={(e) => setSearchQ(e.target.value)} placeholder="Where do you want to go? Varanasi, Kashmir, Nepal..." className="flex-1 bg-transparent outline-none text-white placeholder-blue-100/60 py-2" />
            <button type="submit" className="btn-orange !py-2 !px-5 inline-flex items-center gap-1.5 text-sm">Search</button>
          </form>

          <div className="mt-16 grid grid-cols-2 md:grid-cols-4 gap-5 max-w-4xl mx-auto">
            {stats.map((s) => (
              <div key={s.label} className="rounded-xl bg-white/5 border border-white/10 backdrop-blur px-4 py-5">
                <div className="text-2xl md:text-3xl font-bold text-orange-300">{s.value}</div>
                <div className="text-xs md:text-sm text-blue-100/80 mt-1">{s.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* WHY CHOOSE */}
      <section className="section bg-white">
        <div className="max-w-7xl mx-auto px-5 lg:px-8">
          <div className="text-center max-w-2xl mx-auto">
            <span className="eyebrow">Why Choose Us</span>
            <h2 className="font-serif text-3xl md:text-4xl font-bold text-navy mt-4">Curated Journeys, Trusted Service</h2>
            <p className="text-slate-600 mt-3">We handle everything — permits, hotels, cabs, guides and comfort — so you focus on making memories.</p>
          </div>
          <div className="grid md:grid-cols-4 gap-6 mt-12">
            {[
              { icon: ShieldCheck, title: 'Safe & Verified', desc: 'Licensed operator, verified vehicles & background-checked drivers.' },
              { icon: Wallet, title: 'Best Price Promise', desc: 'Transparent pricing, no hidden charges. Corporate GST invoices available.' },
              { icon: HeadphonesIcon, title: '24/7 Support', desc: 'WhatsApp / Phone support any hour of the day, even on your trip.' },
              { icon: Award, title: '12+ Years Experience', desc: 'Trusted by 10,000+ travellers across India & abroad.' },
            ].map((f) => (
              <div key={f.title} className="rounded-2xl p-6 border border-slate-100 bg-white card-lift">
                <div className="w-12 h-12 rounded-full bg-orange-50 flex items-center justify-center"><f.icon className="w-6 h-6 text-orange-500" /></div>
                <h3 className="mt-4 font-semibold text-lg text-navy">{f.title}</h3>
                <p className="text-sm text-slate-600 mt-1.5 leading-relaxed">{f.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* PACKAGES */}
      <section id="packages" className="section bg-slate-50">
        <div className="max-w-7xl mx-auto px-5 lg:px-8">
          <div className="text-center max-w-2xl mx-auto">
            <span className="eyebrow">Our Packages</span>
            <h2 className="font-serif text-3xl md:text-4xl font-bold text-navy mt-4">Popular Tour Destinations</h2>
            <p className="text-slate-600 mt-3">Hand-crafted itineraries for spiritual yatras, family holidays & corporate tours across India.</p>
          </div>

          <div className="mt-8 flex flex-col lg:flex-row gap-4 items-center justify-between">
            <div className="flex flex-wrap items-center justify-center gap-2.5">
              {categories.map((c) => (
                <button key={c} onClick={() => setActiveCat(c)} className={`chip ${activeCat === c ? 'chip-active' : ''}`}>{c}</button>
              ))}
            </div>
            <div className="relative w-full lg:max-w-xs">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <Input value={searchQ} onChange={(e) => setSearchQ(e.target.value)} placeholder="Search Varanasi, Nepal..." className="pl-9 bg-white" />
            </div>
          </div>

          <div className="mt-10 grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {featured.map((p) => <PackageCard key={p.id} pkg={p} />)}
          </div>

          {featured.length === 0 && (
            <div className="text-center py-16 text-slate-500">No packages match your search. Try a different keyword or category.</div>
          )}

          <div className="text-center mt-10">
            <Link to="/packages" className="btn-outline-navy inline-flex items-center gap-2">View All {packages.length} Packages <ArrowRight className="w-4 h-4" /></Link>
          </div>
        </div>
      </section>

      {/* VEHICLES */}
      <section id="vehicles" className="section bg-white">
        <div className="max-w-7xl mx-auto px-5 lg:px-8">
          <div className="text-center max-w-2xl mx-auto">
            <span className="eyebrow">Our Fleet</span>
            <h2 className="font-serif text-3xl md:text-4xl font-bold text-navy mt-4">Comfortable Tourist Vehicles</h2>
            <p className="text-slate-600 mt-3">From sedans to luxury coaches — clean, AC & well-maintained vehicles for every group size. Corporate & Government tenders welcome.</p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6 mt-10">
            {vehicles.map((v) => (
              <div key={v.name} className="rounded-2xl overflow-hidden bg-white border border-slate-100 card-lift">
                <div className="h-44 img-zoom bg-slate-100">
                  <img src={v.image} alt={v.name} className="w-full h-full object-cover" loading="lazy" onError={(e) => { e.currentTarget.src = 'https://images.unsplash.com/photo-1570125909232-eb263c188f7e'; }} />
                </div>
                <div className="p-5">
                  <h4 className="font-serif text-lg font-bold text-navy">{v.name}</h4>
                  <p className="text-xs text-slate-500 mt-1.5 leading-relaxed">{v.desc}</p>
                  <a href={whatsappLink(`I want to book ${v.name} for my trip`)} target="_blank" rel="noreferrer" className="mt-4 inline-flex items-center gap-1.5 text-sm font-semibold text-orange-500 hover:text-orange-600">Enquire <ArrowRight className="w-3.5 h-3.5" /></a>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section className="section bg-slate-50">
        <div className="max-w-7xl mx-auto px-5 lg:px-8">
          <div className="text-center max-w-2xl mx-auto">
            <span className="eyebrow">Happy Travellers</span>
            <h2 className="font-serif text-3xl md:text-4xl font-bold text-navy mt-4">What Our Guests Say</h2>
          </div>
          <div className="grid md:grid-cols-3 gap-6 mt-10">
            {testimonials.map((t) => (
              <div key={t.name} className="rounded-2xl bg-white border border-slate-100 p-6 card-lift">
                <div className="flex gap-1 mb-3">
                  {Array.from({ length: t.rating }).map((_, i) => <Star key={i} className="w-4 h-4 fill-orange-400 text-orange-400" />)}
                </div>
                <p className="text-slate-700 leading-relaxed text-[15px]">“{t.text}”</p>
                <div className="mt-5 pt-4 border-t border-slate-100 flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-orange-100 text-orange-600 font-bold flex items-center justify-center">{t.name.charAt(0)}</div>
                  <div>
                    <div className="font-semibold text-navy">{t.name}</div>
                    <div className="text-xs text-slate-500">{t.location}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CONTACT */}
      <section id="contact" className="section bg-white">
        <div className="max-w-7xl mx-auto px-5 lg:px-8">
          <div className="text-center max-w-2xl mx-auto">
            <span className="eyebrow">Get in Touch</span>
            <h2 className="font-serif text-3xl md:text-4xl font-bold text-navy mt-4">Plan Your Journey Today</h2>
            <p className="text-slate-600 mt-3">Fill the form or call/WhatsApp us directly. We reply within minutes!</p>
          </div>

          <div className="grid lg:grid-cols-5 gap-8 mt-12">
            <div className="lg:col-span-2 bg-navy text-white rounded-2xl p-8" style={{ backgroundColor: '#0b1e3d' }}>
              <h3 className="font-serif text-2xl font-bold">{brand.name}</h3>
              <div className="mt-6 space-y-5">
                <div className="flex gap-3"><MapPin className="w-5 h-5 text-orange-400 mt-0.5 shrink-0" /><div><div className="font-semibold">Address</div><p className="text-sm text-blue-100/80">{brand.address}</p></div></div>
                <div className="flex gap-3"><Phone className="w-5 h-5 text-orange-400 mt-0.5 shrink-0" /><div><div className="font-semibold">Phone / WhatsApp</div><a href={`tel:${brand.phoneRaw}`} className="block text-sm text-blue-100/80 hover:text-orange-300">{brand.phone1}</a><a href="tel:+917985182238" className="block text-sm text-blue-100/80 hover:text-orange-300">{brand.phone2}</a></div></div>
                <div className="flex gap-3"><Mail className="w-5 h-5 text-orange-400 mt-0.5 shrink-0" /><div><div className="font-semibold">Email</div><a href={`mailto:${brand.email}`} className="text-sm text-blue-100/80 hover:text-orange-300 break-all">{brand.email}</a></div></div>
                <div className="flex gap-3"><Facebook className="w-5 h-5 text-orange-400 mt-0.5 shrink-0" /><div><div className="font-semibold">Website & Social</div><a href={`https://${brand.website}`} className="block text-sm text-blue-100/80 hover:text-orange-300">{brand.website}</a><a href={brand.facebook} target="_blank" rel="noreferrer" className="block text-sm text-blue-100/80 hover:text-orange-300">Facebook Page →</a></div></div>
              </div>
            </div>

            <form onSubmit={onSubmit} className="lg:col-span-3 rounded-2xl bg-white border border-slate-100 p-8 shadow-sm space-y-4">
              <h3 className="font-serif text-2xl font-bold text-navy">Quick Enquiry Form</h3>
              <div className="grid md:grid-cols-2 gap-4">
                <div><Label>Your Name *</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1.5" placeholder="Rajesh Sharma" required /></div>
                <div><Label>Phone / WhatsApp *</Label><Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="mt-1.5" placeholder="+91 98xxxxxxxx" required /></div>
                <div><Label>Email</Label><Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="mt-1.5" placeholder="you@example.com" /></div>
                <div>
                  <Label>Destination / Package</Label>
                  <Select value={form.destination} onValueChange={(v) => setForm({ ...form, destination: v })}>
                    <SelectTrigger className="mt-1.5"><SelectValue placeholder="Select destination" /></SelectTrigger>
                    <SelectContent>
                      {['Varanasi','Ayodhya','Prayagraj','Char Dham Yatra','Kedarnath','Kashmir','Goa','Kerala','Rajasthan Circuit','Leh-Ladakh','Vaishno Devi','Shirdi Sai','Tirupati','Nepal - Kathmandu & Pokhara','Nepal - Muktinath','Nepal - Lumbini','Guwahati','Other / Custom'].map((d) => (
                        <SelectItem key={d} value={d}>{d}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div><Label>Travel Date</Label><Input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} className="mt-1.5" /></div>
                <div><Label>No. of Travellers</Label><Input type="number" min="1" value={form.travellers} onChange={(e) => setForm({ ...form, travellers: e.target.value })} className="mt-1.5" placeholder="2" /></div>
              </div>
              <div><Label>Message</Label><Textarea value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} className="mt-1.5" placeholder="Any custom requirements..." rows={4} /></div>
              <button type="submit" className="btn-orange w-full inline-flex items-center justify-center gap-2">Send Enquiry <ArrowRight className="w-4 h-4" /></button>
            </form>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
